// Tab 1 · Where it shows (LD-1, planner/lender-hub/lender-directory-plan-2026-10-20.md §4, §4a): the Website and Apply
// now, each with the ★ fields it needs as the Hub API decides them (one rule file, api2 listing-rules.ts), Publish /
// Unpublish (admins), the published version and what changed since. The website and the web app read only what is
// published; an edit here shows nowhere until it is published again.
import { useCallback, useEffect, useState } from 'react'
import { Link } from 'react-router-dom'
import { CheckCircle2, Circle, Loader2 } from 'lucide-react'
import { Badge, ConfirmDialog, formatDateTime, notify } from '@/kit/ui'
import { ApiError, apiMode } from '@/lib/hubApi'
import { listingApi } from '@/lib/listingApi'
import { useCurrentUser } from '@/shell/useCurrentUser'
import type { Channel, Lender, LenderListing, ListingCheck } from '@/types'

/** Where each kind of missing field is filled in. */
const FIX: Record<ListingCheck['fix'], { label: string; tab?: string }> = {
  basics: { label: 'Edit basics' }, details: { label: 'Edit details' }, logo: { label: 'Upload logo (Basics)' }, api: { label: 'Edit API' }, products: { label: 'tab 2 · Products', tab: 'products' },
}
const WHERE: Record<Channel, string> = {
  website: 'the lending partners page and the Home strip',
  applynow: 'its own Apply now journey (apply-now/<short name>)',
}

export function WhereItShows({ lender }: { lender: Lender }) {
  const { can } = useCurrentUser()
  const [view, setView] = useState<LenderListing | null>(null)
  const [error, setError] = useState('')
  const [asking, setAsking] = useState<{ channel: Channel; publish: boolean } | null>(null)
  const [busy, setBusy] = useState(false)

  const load = useCallback(async () => {
    try { setView(await listingApi.view(lender.slug)); setError('') } catch (err) { setError(err instanceof Error ? err.message : String(err)) }
  }, [lender.slug])
  // again after every change of the lender (a field filled in may complete a checklist)
  useEffect(() => { if (apiMode()) void load() }, [load, lender.updatedAt])

  if (!apiMode()) return <section className="card" aria-label="Where it shows"><h2 className="section-title">Where it shows</h2><p className="text-2xs text-muted">Needs the Hub API (Publish is decided there).</p></section>

  const act = async () => {
    if (!asking) return
    setBusy(true)
    try {
      const next = asking.publish ? await listingApi.publish(lender.slug, asking.channel) : await listingApi.unpublish(lender.slug, asking.channel)
      setView(next)
      notify.success(asking.publish ? `Published to ${asking.channel === 'website' ? 'the website' : 'Apply now'} (version ${next.version})` : 'Unpublished')
      setAsking(null)
    } catch (err) {
      const missing = err instanceof ApiError && Array.isArray(err.extra.missing) ? ` Missing: ${(err.extra.missing as string[]).join(', ')}.` : ''
      notify.error(asking.publish ? 'Not published' : 'Not unpublished', (err instanceof Error ? err.message : String(err)) + missing)
      await load()
    } finally { setBusy(false) }
  }

  const place = asking && view?.places.find((p) => p.channel === asking.channel)
  return (
    <section className="card mt-3" aria-label="Where it shows">
      <div className="mb-2 flex flex-wrap items-center justify-between gap-2">
        <h2 className="section-title !mb-0">Where it shows</h2>
        {view?.version ? <span className="text-2xs text-muted">Published copy: version {view.version} · {formatDateTime(view.publishedAt!)} · {view.publishedBy}</span> : null}
      </div>
      {error ? <p className="text-2xs text-red-600">{error}</p> : null}
      {!view ? <p className="text-2xs text-muted"><Loader2 size={12} className="inline animate-spin" /> Loading…</p> : (
        <>
          {!view.lenderActive ? <p className="mb-2 rounded bg-amber-50 px-2 py-1 text-2xs text-amber-800 dark:bg-amber-950 dark:text-amber-200">{lender.status === 'draft' ? 'A draft: Go live first, then publish.' : 'Switched Off: shown nowhere until it is switched On again.'}</p> : null}
          {view.changedSincePublish ? <p className="mb-2 rounded bg-sky-50 px-2 py-1 text-2xs text-sky-800 dark:bg-sky-950 dark:text-sky-200">Changed since the last Publish: the website and Apply now still show version {view.version}. Publish again to show the change.</p> : null}
          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
            {view.places.map((p) => {
              const missing = p.checks.filter((c) => !c.ok)
              return (
                <div key={p.channel} className="rounded border border-slate-200 p-3 dark:border-slate-700">
                  <div className="mb-1 flex items-center justify-between gap-2">
                    <b>{p.label}</b>
                    {p.published ? <Badge tone="success">Published</Badge> : p.ready ? <Badge tone="info">Ready</Badge> : <Badge tone="warning">{missing.length} missing</Badge>}
                  </div>
                  <p className="mb-2 text-2xs text-muted">Shows it on {WHERE[p.channel]}.</p>
                  <ul className="mb-3 space-y-1 text-xs">
                    {p.checks.map((c) => (
                      <li key={c.key} className="flex items-start gap-1.5">
                        {c.ok ? <CheckCircle2 size={14} className="mt-0.5 shrink-0 text-emerald-600" /> : <Circle size={14} className="mt-0.5 shrink-0 text-amber-600" />}
                        <span className={c.ok ? 'text-muted' : ''}>{c.label}{!c.ok ? <span className="text-2xs text-muted"> · {FIX[c.fix].tab ? <Link to={`/lenders/${lender.slug}/${FIX[c.fix].tab}`}>{FIX[c.fix].label}</Link> : FIX[c.fix].label}</span> : null}</span>
                      </li>
                    ))}
                  </ul>
                  <div className="flex flex-wrap gap-2">
                    <button type="button" className="btn btn-primary btn-sm" disabled={!can('lenders.activate') || !p.ready || !view.lenderActive || (p.published && !view.changedSincePublish)}
                      title={!can('lenders.activate') ? 'An admin publishes' : !p.ready ? 'Fill in the missing fields first' : undefined}
                      onClick={() => setAsking({ channel: p.channel, publish: true })}>{p.published ? 'Publish changes' : 'Publish'}</button>
                    {p.published ? <button type="button" className="btn btn-secondary btn-sm" disabled={!can('lenders.activate')} onClick={() => setAsking({ channel: p.channel, publish: false })}>Unpublish</button> : null}
                  </div>
                </div>
              )
            })}
          </div>
          {view.versions.length ? (
            <details className="mt-2 text-2xs text-muted">
              <summary className="cursor-pointer">Every Publish / Unpublish ({view.versions.length})</summary>
              <ul className="mt-1 space-y-0.5">{view.versions.map((v, i) => <li key={i}>{formatDateTime(v.at)} · {v.by} · {v.action === 'publish' ? 'Published to' : 'Unpublished from'} {v.channel === 'website' ? 'Website' : 'Apply now'} (version {v.version})</li>)}</ul>
            </details>
          ) : null}
        </>
      )}
      <ConfirmDialog
        isOpen={asking !== null}
        onClose={() => setAsking(null)}
        busy={busy}
        type={asking?.publish ? 'info' : 'danger'}
        title={asking ? `${asking.publish ? 'Publish' : 'Unpublish'} ${lender.name} ${asking.publish ? 'to' : 'from'} ${place?.label ?? ''}?` : ''}
        message={asking?.publish
          ? `Customers see these facts on ${WHERE[asking.channel]} within minutes. Every place it is on shows this copy. Logged with your name.`
          : asking ? `It leaves ${WHERE[asking.channel]} within minutes. Its versions stay in the log.` : ''}
        confirmText={asking?.publish ? 'Publish' : 'Unpublish'}
        onConfirm={act}
      />
    </section>
  )
}
